import { FC, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { DEFAULT_MEDICAL_IMAGE_HEIGHT } from '@/shared/config';
import {
  ANNOTATION_PRESENTATION,
  formatAnnotationAngle,
  formatAnnotationLength,
  makeAnnotationAngleGeometry,
  makeAnnotationAngleLabel,
  makeAnnotationArrowGeometry,
  makeAnnotationRulerLabel,
  measureAnnotationLength,
  type AnnotationPresentationVariant,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import type { Annotation } from '../AnnotationsLayer/Annotation';

import styles from './AnnotationsOverlay.module.scss';

type AnnotationsOverlayProps = {
  annotations: Annotation[];
  heightPx: number;
  widthPx: number;
  className?: string;
  scaleMmPerPixel?: number;
  variant?: AnnotationPresentationVariant;
};

type Point = { x: number; y: number };

const resolveAngleGeometry = (
  annotation: Extract<Annotation, { kind: 'angle' }>,
) => ({
  vertex: { x: annotation.x2, y: annotation.y2 },
  rayStart: { x: annotation.x1, y: annotation.y1 },
  rayEnd: { x: annotation.x3, y: annotation.y3 },
});

export const AnnotationsOverlay: FC<AnnotationsOverlayProps> = (props) => {
  const {
    annotations,
    className,
    heightPx,
    scaleMmPerPixel,
    variant = 'thumbnail',
    widthPx,
  } = props;

  const svgRef = useRef<SVGSVGElement>(null);
  const [displayHeightPx, setDisplayHeightPx] = useState(0);
  const hasAnnotations = annotations.length > 0 && widthPx > 0 && heightPx > 0;

  useEffect(() => {
    const element = svgRef.current;

    if (!element || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      setDisplayHeightPx(entries[0]?.contentRect.height ?? 0);
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [hasAnnotations]);

  if (!hasAnnotations) {
    return null;
  }

  const viewBoxPerDisplayPx = displayHeightPx
    ? heightPx / displayHeightPx
    : heightPx /
      (variant === 'editor' ? heightPx : DEFAULT_MEDICAL_IMAGE_HEIGHT);

  const presentation = ANNOTATION_PRESENTATION[variant];
  const fontSize = presentation.labelSizePx * viewBoxPerDisplayPx;
  const strokeWidth = presentation.strokeWidthPx * viewBoxPerDisplayPx;
  const handleRadius = presentation.handleRadiusPx * viewBoxPerDisplayPx;
  const arrowHeadLength = presentation.arrowHeadLengthPx * viewBoxPerDisplayPx;

  const drawHandle = (point: Point, radiusFactor = 1) => (
    <circle
      className={styles.handle}
      cx={point.x}
      cy={point.y}
      r={handleRadius * radiusFactor}
    />
  );

  return (
    <svg
      ref={svgRef}
      data-component="annotations-overlay"
      className={cn(styles.overlay, className)}
      viewBox={`0 0 ${widthPx} ${heightPx}`}
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      style={{ color: presentation.color }}
    >
      {annotations.map((annotation, index) => {
        switch (annotation.kind) {
          case 'ruler': {
            const start = { x: annotation.x1, y: annotation.y1 };
            const end = { x: annotation.x2, y: annotation.y2 };
            const label = scaleMmPerPixel
              ? formatAnnotationLength(
                  measureAnnotationLength({ end, scaleMmPerPixel, start }),
                )
              : null;
            const labelPosition = makeAnnotationRulerLabel({
              end,
              fontSize,
              start,
            });

            return (
              <g key={index}>
                <line
                  className={styles.line}
                  strokeWidth={strokeWidth}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                />
                {drawHandle(start)}
                {drawHandle(end)}
                {label && (
                  <text
                    className={styles.label}
                    fontSize={fontSize}
                    transform={`rotate(${labelPosition.rotationDeg} ${labelPosition.x} ${labelPosition.y})`}
                    x={labelPosition.x}
                    y={labelPosition.y}
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          }
          case 'arrow': {
            const start = { x: annotation.x1, y: annotation.y1 };
            const end = { x: annotation.x2, y: annotation.y2 };
            const length = Math.hypot(end.x - start.x, end.y - start.y);

            if (!length) {
              return null;
            }

            const { base, left, right } = makeAnnotationArrowGeometry({
              end,
              headLength: arrowHeadLength,
              start,
            });

            return (
              <g key={index}>
                <line
                  className={styles.line}
                  strokeWidth={strokeWidth}
                  x1={start.x}
                  y1={start.y}
                  x2={base.x}
                  y2={base.y}
                />
                <polygon
                  className={styles.arrowhead}
                  points={`${end.x},${end.y} ${left.x},${left.y} ${right.x},${right.y}`}
                />
                {drawHandle(
                  start,
                  presentation.arrowStartRadiusPx / presentation.handleRadiusPx,
                )}
              </g>
            );
          }
          case 'angle': {
            const { rayEnd, rayStart, vertex } =
              resolveAngleGeometry(annotation);
            const arc = makeAnnotationAngleGeometry({
              end: rayEnd,
              start: rayStart,
              vertex,
            });
            const labelPosition = arc.radius
              ? makeAnnotationAngleLabel({ ...arc, fontSize })
              : { rotationDeg: 0, x: vertex.x, y: vertex.y + fontSize * 0.05 };

            return (
              <g key={index}>
                <line
                  className={styles.line}
                  strokeWidth={strokeWidth}
                  x1={vertex.x}
                  y1={vertex.y}
                  x2={rayStart.x}
                  y2={rayStart.y}
                />
                <line
                  className={styles.line}
                  strokeWidth={strokeWidth}
                  x1={vertex.x}
                  y1={vertex.y}
                  x2={rayEnd.x}
                  y2={rayEnd.y}
                />
                {!!arc.radius && (
                  <path
                    className={styles.line}
                    strokeWidth={strokeWidth}
                    d={`M ${arc.start.x} ${arc.start.y} A ${arc.radius} ${arc.radius} 0 0 ${arc.sweep} ${arc.end.x} ${arc.end.y}`}
                  />
                )}
                {drawHandle(rayStart)}
                {drawHandle(rayEnd)}
                {drawHandle(vertex)}
                <text
                  className={styles.label}
                  fontSize={fontSize}
                  transform={`rotate(${labelPosition.rotationDeg} ${labelPosition.x} ${labelPosition.y})`}
                  x={labelPosition.x}
                  y={labelPosition.y}
                >
                  {formatAnnotationAngle(arc.degrees)}
                </text>
              </g>
            );
          }
          case 'line':
            return (
              <g key={index}>
                <line
                  className={styles.line}
                  strokeWidth={strokeWidth}
                  x1={annotation.x1}
                  y1={annotation.y1}
                  x2={annotation.x2}
                  y2={annotation.y2}
                />
                {drawHandle({ x: annotation.x1, y: annotation.y1 })}
                {drawHandle({ x: annotation.x2, y: annotation.y2 })}
              </g>
            );
          default:
            return null;
        }
      })}
    </svg>
  );
};
